import { useEffect, useId, useRef, useState } from 'react';
import { CAL_FILTER_FREE } from '@/calendar/pwaCalendarUtils';

export type CalResourceComboOption = {
  id: string;
  name: string;
  color?: string | null;
  agentName?: string;
};

type Props = {
  resources: CalResourceComboOption[];
  value: string;
  onChange: (value: string) => void;
};

function Dot({ color }: { color?: string | null }) {
  const bg = color && /^#[0-9A-Fa-f]{6}$/.test(color) ? color : 'var(--pwa-text-dim)';
  return <span className="cal-resource-combo__dot" style={{ background: bg }} aria-hidden />;
}

function labelFor(value: string, resources: CalResourceComboOption[]): string {
  if (value === '') return 'Todos los recursos';
  if (value === CAL_FILTER_FREE) return 'Solo libres';
  const r = resources.find((x) => x.id === value);
  if (!r) return 'Recurso';
  return r.agentName ? `${r.name} · ${r.agentName}` : r.name;
}

export function CalResourceCombo({ resources, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedResource = resources.find((r) => r.id === value);
  const selectedColor =
    value === CAL_FILTER_FREE ? '#059669'
    : value === '' ? null
    : selectedResource?.color;

  function pick(next: string) {
    onChange(next);
    setOpen(false);
  }

  return (
    <div className="cal-resource-combo" ref={rootRef}>
      <span className="cal-resource-combo__label" id={`${listId}-label`}>
        Recurso
      </span>
      <button
        type="button"
        className="cal-resource-combo__trigger"
        aria-labelledby={`${listId}-label`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="cal-resource-combo__trigger-main">
          {value === CAL_FILTER_FREE ?
            <span className="cal-resource-combo__free-badge" aria-hidden>
              Libre
            </span>
          : <Dot color={selectedColor} />}
          <span className="cal-resource-combo__trigger-text">{labelFor(value, resources)}</span>
        </span>
        <span className="cal-resource-combo__chevron" aria-hidden>
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open ?
        <ul id={listId} className="cal-resource-combo__list" role="listbox" aria-label="Filtrar por recurso">
          <li role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={value === ''}
              className={[
                'cal-resource-combo__option',
                value === '' ? 'cal-resource-combo__option--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => pick('')}
            >
              <Dot color={null} />
              <span>Todos los recursos</span>
            </button>
          </li>
          <li role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={value === CAL_FILTER_FREE}
              className={[
                'cal-resource-combo__option',
                'cal-resource-combo__option--free',
                value === CAL_FILTER_FREE ? 'cal-resource-combo__option--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => pick(CAL_FILTER_FREE)}
            >
              <span className="cal-resource-combo__free-badge" aria-hidden>
                Libre
              </span>
              <span>
                Solo libres
                <span className="cal-resource-combo__option-hint">Disponibles, sin reserva</span>
              </span>
            </button>
          </li>
          {resources.map((resource) => (
            <li key={resource.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === resource.id}
                className={[
                  'cal-resource-combo__option',
                  value === resource.id ? 'cal-resource-combo__option--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => pick(resource.id)}
              >
                <Dot color={resource.color} />
                <span>
                  {resource.name}
                  {resource.agentName ?
                    <span className="cal-resource-combo__option-hint">{resource.agentName}</span>
                  : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      : null}
    </div>
  );
}
